import outdent from "outdent"
import { print } from "graphql/language/printer"
import { resolve_relations } from "storyblok/resolveRelations"

/**
 * A utility function to fetch data from the Storyblok GraphQL API
 *
 * @param {string} query A template literal containing the query to send to the Storyblok Graphql API
 * @param {object} options An optional object containing variables used in the query and a preview value which returns unpublished (saved) content if true
 * @param {boolean} debug Log any query errors that occurred to console. This is false by default to avoid flooding the console when upstream errors create obvious query errors
 * @returns {Promise<object>} The requested data. An error is logged to the console if the query has an error or the fetch fails
 */
export default async function fetchQuery(query, { variables, preview } = {}, debug = false) {
  query = typeof query === "object" ? print(query) : query

  try {
    if (!query.includes("resolve_links:") && !query.includes("DatasourceEntries")) {
      throw new Error(
        `The query must include a resolve_links arg. This is to ensure the most up to date urls are always retrieved from Storyblok.

        Provided Query:

        ${query}

        `,
      )
    }

    const queryRequest = async ({ token, version }) => {
      if (!token) {
        throw new Error(`Missing Storyblok API token for ${version} content`)
      }

      return await fetch(`https://gapi-us.storyblok.com/v1/api`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Token: token,
          Version: version,
        },
        body: JSON.stringify({
          query: query,
          variables: {
            ...variables,
            resolve_relations,
          },
        }),
      })
    }

    async function delay(ms) {
      return await new Promise((resolve) => setTimeout(resolve, ms))
    }

    /**
     * Retry queries until a successful response is retrieved
     *
     * @param {Function} queryRequest A query request to execute
     * @param {integer} attempt The current retry attempt
     * @param {integer} baseDelay The initial delay in milliseconds
     * @returns {Promise<object>} The query request's response
     */
    async function maybeRetryQuery(queryRequest, attempt = 0, baseDelay = 250) {
      const response = await queryRequest()

      if (response.status === 429) {
        if (attempt >= 4) {
          throw new Error("Storyblok rate limit exceeded after 5 attempts")
        }

        const timeToWait = baseDelay * 2 ** attempt

        const name = getQueryName(query)

        console.warn(`\n ${name} 429 Rate limit exceeded. Retrying in ${timeToWait}ms`)
        await delay(timeToWait)
        return await maybeRetryQuery(queryRequest, attempt + 1, baseDelay)
      }

      if (response.status !== 200) {
        const error = new Error(`Response code: ${response.status}`)
        error.status = response.status
        throw error
      }

      return response
    }

    let response

    try {
      response = await maybeRetryQuery(() => queryRequest(getStoryblokRequestConfig(preview)))
    } catch (error) {
      if (!preview || !isStoryblokAuthError(error)) {
        throw error
      }

      console.warn("Storyblok draft request was rejected; falling back to published content for preview render.")
      response = await maybeRetryQuery(() => queryRequest(getStoryblokRequestConfig(false)))
    }

    const { data, errors } = await response.json()
    if (errors && debug) {
      console.error(
        outdent`

      ++++++++++++++++++++++++++++++++++++++
      --------------------------------------
      INVALID QUERY REQUEST
      --------------------------------------

      --------------------------------------
      PREVIEW MODE ENABLED:

      ${Boolean(preview)}

      --------------------------------------
      QUERY:

      ${query}

      --------------------------------------
      VARIABLES:

      ${JSON.stringify({ variables }, null, 2)}

      --------------------------------------
      ERRORS:

      ${JSON.stringify({ errors }, null, 2)}

      --------------------------------------
      ++++++++++++++++++++++++++++++++++++++

      `,
      )
    }

    // avoid returning unserializable data
    return data ?? null
  } catch (error) {
    console.error(`fetch error: ${error}`)
  }
}

/**
 * Given a query string, parses the query and returns a referencable name for use in logs
 *
 * @param {String} query The query to parse
 * @returns {string} A referencable name for the query
 */
function getQueryName(query) {
  const queryRegex = []
  void query.replace(/(?:query\s*)([A-Z]\w*)?(?:\(.*?\))?\s*{\s*(\w+)/g, (...args) => {
    if (args[1]) {
      queryRegex.push(args[1])
    } else if (args[2]) {
      queryRegex.push(args[2])
    } else {
      queryRegex.push("Invalid query name")
    }
  })
  const [name] = queryRegex

  // TODO: fix this to always detect something more usable
  return name ?? "Unknown"
}

function getStoryblokRequestConfig(preview) {
  const publishedToken = process.env.NEXT_PUBLIC_STORYBLOK_API_TOKEN

  if (!preview) {
    return {
      token: publishedToken,
      version: "published",
    }
  }

  return {
    token:
      process.env.STORYBLOK_DRAFT_API_TOKEN ??
      process.env.STORYBLOK_PREVIEW_API_TOKEN ??
      process.env.STORYBLOK_API_TOKEN ??
      publishedToken,
    version: "draft",
  }
}

function isStoryblokAuthError(error) {
  return error?.status === 401 || error?.status === 403
}
